import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, LogOut, Loader2, Upload, Calendar, Users,
  Package, Inbox, Mail, ExternalLink, X, Ban, AlertTriangle, Clock,
} from "lucide-react";
import api, { formatApiErrorDetail, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Navbar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const CATEGORIES = ["Viaggio", "Concerto", "Visita Guidata", "Escursione", "Matrimonio"];
const EMPTY = { title: "", category: "Viaggio", description: "", event_date: "", location: "", price: "", seats: "", min_participants: "", booking_deadline: "", image_url: "" };

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "";
const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("events");
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [cancelId, setCancelId] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [ev, bk, ct] = await Promise.all([
        api.get("/events?all=true"),
        api.get("/bookings"),
        api.get("/contacts"),
      ]);
      setEvents(ev.data);
      setBookings(bk.data);
      setContacts(ct.data);
    } catch (err) {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (ev) => {
    setEditing(ev.id);
    setForm({
      title: ev.title, category: ev.category, description: ev.description,
      event_date: ev.event_date ? ev.event_date.slice(0, 10) : "",
      location: ev.location || "", price: String(ev.price),
      seats: ev.seats ? String(ev.seats) : "",
      min_participants: ev.min_participants ? String(ev.min_participants) : "",
      booking_deadline: ev.booking_deadline ? ev.booking_deadline.slice(0, 10) : "",
      image_url: ev.image_url || "",
    });
    setDialogOpen(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price),
      seats: form.seats ? parseInt(form.seats, 10) : null,
      min_participants: form.min_participants ? parseInt(form.min_participants, 10) : 0,
      booking_deadline: form.booking_deadline || null,
      event_date: form.event_date,
    };
    try {
      if (editing) {
        await api.put(`/events/${editing}`, payload);
        toast.success("Proposta aggiornata");
      } else {
        await api.post("/events", payload);
        toast.success("Proposta creata");
      }
      setDialogOpen(false);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/events/${deleteId}`);
      toast.success("Proposta eliminata");
      setDeleteId(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const confirmCancel = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post(`/events/${cancelId}/cancel`);
      toast.success(`Evento annullato. ${data.refunded} rimborsi effettuati.`);
      if (data.failed) toast.error(`${data.failed} rimborsi falliti — controlla Stripe.`);
      setCancelId(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCancelling(false);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const tabs = [
    { key: "events", label: "Proposte", icon: Package, count: events.length },
    { key: "bookings", label: "Prenotazioni", icon: Inbox, count: bookings.length },
    { key: "contacts", label: "Messaggi", icon: Mail, count: contacts.length },
  ];

  const inputCls =
    "w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder:text-white/40 focus:border-[#FFF200] focus:outline-none transition-colors";

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-white/50 text-sm">{user?.email}</span>
            <button
              data-testid="logout-btn"
              onClick={doLogout}
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-4 py-2 rounded-full text-sm transition-colors"
            >
              <LogOut size={15} /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold">Pannello di controllo</h1>
            <p className="text-white/50 mt-1">Gestisci viaggi, eventi e prenotazioni</p>
          </div>
          {tab === "events" && (
            <button
              data-testid="new-event-btn"
              onClick={openCreate}
              className="flex items-center gap-2 bg-[#FFF200] text-black font-semibold px-5 py-3 rounded-full hover:bg-[#FFF200]/90 transition-colors"
            >
              <Plus size={18} /> Nuova proposta
            </button>
          )}
        </div>

        <div className="flex gap-2 mb-8 border-b border-white/10">
          {tabs.map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.key
                  ? "border-[#FFF200] text-[#FFF200]"
                  : "border-transparent text-white/50 hover:text-white"
              }`}
            >
              <t.icon size={16} /> {t.label}
              <span className="text-xs bg-white/10 rounded-full px-2 py-0.5">{t.count}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-white/40">
            <Loader2 className="animate-spin mx-auto mb-3" size={28} /> Caricamento...
          </div>
        ) : (
          <>
            {tab === "events" && (
              <div className="grid gap-4" data-testid="admin-events-list">
                {events.length === 0 && (
                  <p className="text-white/40 py-10 text-center">Nessuna proposta. Creane una!</p>
                )}
                {events.map((ev) => {
                  const cancelled = ev.status === "cancelled";
                  const minReq = ev.min_participants || 0;
                  const paid = ev.paid_participants || 0;
                  const reached = minReq === 0 || paid >= minReq;
                  return (
                  <div
                    key={ev.id}
                    data-testid={`admin-event-${ev.id}`}
                    className={`flex items-center gap-4 bg-[#121212] border rounded-xl p-4 ${
                      cancelled ? "border-red-500/30 opacity-70" : "border-white/10"
                    }`}
                  >
                    <div className="w-24 h-16 rounded-lg overflow-hidden bg-black shrink-0">
                      {ev.image_url ? (
                        <img src={fileUrl(ev.image_url)} alt={ev.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/20 text-xs">
                          no img
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-[#FFF200] bg-[#FFF200]/10 px-2 py-0.5 rounded-full uppercase tracking-wide font-bold">
                          {ev.category}
                        </span>
                        {cancelled ? (
                          <span className="text-xs text-red-300 bg-red-500/15 px-2 py-0.5 rounded-full font-semibold">
                            Annullato
                          </span>
                        ) : !ev.is_active ? (
                          <span className="text-xs text-white/40 bg-white/5 px-2 py-0.5 rounded-full">
                            nascosto
                          </span>
                        ) : null}
                      </div>
                      <h3 className="font-semibold mt-1 truncate">{ev.title}</h3>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/50 mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar size={12} /> {fmtDate(ev.event_date)}
                        </span>
                        <span className="text-[#FFF200] font-semibold">€{Number(ev.price).toFixed(2)}</span>
                        {ev.booking_deadline && (
                          <span className="flex items-center gap-1">
                            <Clock size={12} /> chiusura {fmtDate(ev.booking_deadline)}
                          </span>
                        )}
                      </div>
                      {minReq > 0 && (
                        <div className="mt-2 flex items-center gap-2">
                          <span
                            className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
                              reached
                                ? "bg-emerald-500/15 text-emerald-300"
                                : "bg-amber-500/15 text-amber-300"
                            }`}
                          >
                            <Users size={12} /> {paid}/{minReq} persone
                          </span>
                          {!reached && !cancelled && (
                            <span className="text-amber-300/70 text-xs flex items-center gap-1">
                              <AlertTriangle size={11} /> minimo non raggiunto
                            </span>
                          )}
                        </div>
                      )}
                      {cancelled && ev.cancel_reason && (
                        <p className="text-red-300/70 text-xs mt-1">{ev.cancel_reason}</p>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {!cancelled && (
                        <button
                          data-testid={`cancel-event-${ev.id}`}
                          title="Annulla evento e rimborsa"
                          onClick={() => setCancelId(ev.id)}
                          className="w-9 h-9 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 flex items-center justify-center transition-colors"
                        >
                          <Ban size={15} />
                        </button>
                      )}
                      <button
                        data-testid={`edit-event-${ev.id}`}
                        onClick={() => openEdit(ev)}
                        className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        data-testid={`delete-event-${ev.id}`}
                        onClick={() => setDeleteId(ev.id)}
                        className="w-9 h-9 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 flex items-center justify-center transition-colors"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}

            {tab === "bookings" && (
              <div className="overflow-x-auto" data-testid="admin-bookings-list">
                {bookings.length === 0 ? (
                  <p className="text-white/40 py-10 text-center">Nessuna prenotazione ancora.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-white/40 border-b border-white/10">
                        <th className="py-3 px-3 font-medium">Cliente</th>
                        <th className="py-3 px-3 font-medium">Evento</th>
                        <th className="py-3 px-3 font-medium">Pers.</th>
                        <th className="py-3 px-3 font-medium">Importo</th>
                        <th className="py-3 px-3 font-medium">Stato</th>
                        <th className="py-3 px-3 font-medium">Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((b) => (
                        <tr key={b.session_id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-3 px-3">
                            <div className="font-medium">{b.customer_name}</div>
                            <div className="text-white/40 text-xs">{b.customer_email}</div>
                            <div className="text-white/40 text-xs">{b.customer_phone}</div>
                          </td>
                          <td className="py-3 px-3 text-white/70">{b.event_title}</td>
                          <td className="py-3 px-3">{b.participants}</td>
                          <td className="py-3 px-3 text-[#FFF200] font-semibold">
                            €{Number(b.amount).toFixed(2)}
                          </td>
                          <td className="py-3 px-3">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                b.payment_status === "paid"
                                  ? "bg-emerald-500/15 text-emerald-300"
                                  : "bg-white/10 text-white/50"
                              }`}
                            >
                              {b.payment_status === "paid" ? "Pagato" : "In attesa"}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-white/40 text-xs">{fmtDateTime(b.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {tab === "contacts" && (
              <div className="grid gap-4" data-testid="admin-contacts-list">
                {contacts.length === 0 ? (
                  <p className="text-white/40 py-10 text-center">Nessun messaggio ancora.</p>
                ) : (
                  contacts.map((c) => (
                    <div key={c.id} className="bg-[#121212] border border-white/10 rounded-xl p-5">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <span className="font-semibold">{c.name}</span>
                        <span className="text-white/40 text-xs">{fmtDateTime(c.created_at)}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-4 text-xs text-[#FFF200] mt-1">
                        <a href={`mailto:${c.email}`} className="hover:underline">{c.email}</a>
                        {c.phone && <span className="text-white/50">{c.phone}</span>}
                      </div>
                      <p className="text-white/70 mt-3 leading-relaxed">{c.message}</p>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          data-testid="event-form-dialog"
          className="bg-[#0d0d0d] border border-white/10 text-white sm:max-w-lg max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing ? "Modifica proposta" : "Nuova proposta"}
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Compila i dettagli della proposta di viaggio o evento.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={save} className="space-y-4 mt-2">
            <input
              required
              data-testid="event-title"
              placeholder="Titolo"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className={inputCls}
            />

            <div className="grid grid-cols-2 gap-4">
              <select
                data-testid="event-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className={inputCls}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c} className="bg-[#0a0a0a]">
                    {c}
                  </option>
                ))}
              </select>
              <input
                required
                type="date"
                data-testid="event-date"
                value={form.event_date}
                onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                className={`${inputCls} [color-scheme:dark]`}
              />
            </div>

            <input
              data-testid="event-location"
              placeholder="Località / partenza"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className={inputCls}
            />

            <div className="grid grid-cols-2 gap-4">
              <input
                required
                type="number"
                step="0.01"
                min="0"
                data-testid="event-price"
                placeholder="Prezzo € a persona"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className={inputCls}
              />
              <input
                type="number"
                min="0"
                data-testid="event-seats"
                placeholder="Posti disponibili"
                value={form.seats}
                onChange={(e) => setForm({ ...form, seats: e.target.value })}
                className={inputCls}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white/50 text-xs mb-1.5">Minimo persone</label>
                <input
                  type="number"
                  min="0"
                  data-testid="event-min-participants"
                  placeholder="es. 30"
                  value={form.min_participants}
                  onChange={(e) => setForm({ ...form, min_participants: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-white/50 text-xs mb-1.5">Data limite prenotazioni</label>
                <input
                  type="date"
                  data-testid="event-booking-deadline"
                  value={form.booking_deadline}
                  onChange={(e) => setForm({ ...form, booking_deadline: e.target.value })}
                  className={`${inputCls} [color-scheme:dark]`}
                />
              </div>
            </div>
            <p className="text-white/40 text-xs -mt-1">
              Se al termine della data limite il minimo di persone non è raggiunto, l'evento viene
              annullato e tutti i clienti vengono rimborsati automaticamente.
            </p>

            <textarea
              required
              rows={4}
              data-testid="event-description"
              placeholder="Descrizione dell'evento / viaggio"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className={`${inputCls} resize-none`}
            />

            <div>
              <label className="block text-white/60 text-sm mb-2">Immagine</label>
              {form.image_url ? (
                <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden mb-2">
                  <img src={fileUrl(form.image_url)} alt="preview" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    data-testid="remove-image-btn"
                    onClick={() => setForm({ ...form, image_url: "" })}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 hover:bg-black flex items-center justify-center"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : null}
              <label
                data-testid="image-upload-label"
                className="flex items-center justify-center gap-2 border border-dashed border-white/20 rounded-lg py-4 cursor-pointer hover:border-[#FFF200]/50 transition-colors text-white/60"
              >
                {uploading ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <Upload size={18} />
                )}
                {uploading ? "Caricamento..." : form.image_url ? "Cambia immagine" : "Carica immagine"}
                <input
                  type="file"
                  accept="image/*"
                  data-testid="image-upload-input"
                  onChange={handleUpload}
                  className="hidden"
                />
              </label>
            </div>

            <button
              type="submit"
              disabled={saving || uploading}
              data-testid="save-event-btn"
              className="w-full flex items-center justify-center gap-2 bg-[#FFF200] text-black font-semibold py-3.5 rounded-full hover:bg-[#FFF200]/90 transition-colors disabled:opacity-60"
            >
              {saving ? <Loader2 className="animate-spin" size={18} /> : null}
              {editing ? "Salva modifiche" : "Crea proposta"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!cancelId} onOpenChange={(o) => !o && setCancelId(null)}>
        <AlertDialogContent className="bg-[#0d0d0d] border border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Annullare l'evento e rimborsare?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/50">
              Tutti i clienti che hanno pagato verranno rimborsati tramite Stripe e l'evento
              non sarà più visibile sul sito. Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white">
              Indietro
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-cancel-btn"
              disabled={cancelling}
              onClick={(e) => {
                e.preventDefault();
                confirmCancel();
              }}
              className="bg-amber-500 text-black hover:bg-amber-600"
            >
              {cancelling ? "Rimborso in corso..." : "Annulla e rimborsa"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-[#0d0d0d] border border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la proposta?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/50">
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white">
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={confirmDelete}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
